import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, ChevronDown, MessageCircle } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_da187197-cd0a-49f8-adf8-744b68840464/artifacts/ky3myote_Untitled%20design%20%289%29.png";

const navLinks = [
  { name: "Home", path: "/" },
  { name: "About Us", path: "/about" },
  {
    name: "Courses",
    path: "/courses",
    submenu: [
      { name: "Banking Exams", path: "/courses?category=banking" },
      { name: "SSC Exams", path: "/courses?category=ssc" },
      { name: "Railway Exams", path: "/courses?category=railway" },
      { name: "MBA Entrance", path: "/courses?category=mba" },
      { name: "Insurance Exams", path: "/courses?category=insurance" },
      { name: "Other Govt Exams", path: "/courses?category=others" },
    ],
  },
  { name: "Demo Class", path: "/demo-class" },
  { name: "Study Material", path: "/study-material" },
  { name: "Gallery", path: "/gallery" },
  { name: "Notifications", path: "/notifications" },
  { name: "Programs", path: "/programs" },
  { name: "Resources", path: "/resources" },
  { name: "Contact", path: "/contact" },
];

export const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeSubmenu, setActiveSubmenu] = useState(null);
  const location = useLocation();

  const isActive = (path) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path.split("?")[0]);
  };

  return (
    <>
      {/* Top bar */}
      <div className="bg-vedanti-navy text-white text-sm hidden md:block">
        <div className="container-main flex justify-between items-center py-2">
          <div className="flex items-center gap-6">
            <a href="tel:+918308776505" className="flex items-center gap-1.5 hover:text-vedanti-mango transition-colors" data-testid="top-phone-link">
              <Phone size={14} />
              <span>+91 8308776505</span>
            </a>
            <span className="text-white/50">|</span>
            <a href="mailto:vedantiacademy@gmail.com" className="hover:text-vedanti-mango transition-colors" data-testid="top-email-link">
              vedantiacademy@gmail.com
            </a>
          </div>
          <div className="flex items-center gap-4">
            <a href="https://www.instagram.com/vedantiacademy/" target="_blank" rel="noopener noreferrer nofollow" data-testid="social-instagram" className="hover:text-vedanti-mango transition-colors">Instagram</a>
            <a href="https://www.facebook.com/vedantiacademy/" target="_blank" rel="noopener noreferrer nofollow" data-testid="social-facebook" className="hover:text-vedanti-mango transition-colors">Facebook</a>
            <a href="https://www.youtube.com/@vedantiacademy" target="_blank" rel="noopener noreferrer nofollow" data-testid="social-youtube" className="hover:text-vedanti-mango transition-colors">YouTube</a>
          </div>
        </div>
      </div>

      {/* Main nav */}
      <nav className="bg-white sticky top-0 z-50 shadow-sm border-b border-slate-100" data-testid="main-navbar">
        <div className="container-main flex items-center justify-between h-16 md:h-20">
          <Link to="/" className="flex-shrink-0" data-testid="logo-link">
            <img src={LOGO_URL} alt="Vedanti Academy Logo" className="h-16 md:h-20 w-auto" />
          </Link>

          {/* Desktop nav */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <div key={link.name} className="relative group">
                <Link
                  to={link.path}
                  className={`nav-link px-3 py-2 text-sm font-medium flex items-center gap-1 transition-colors ${
                    isActive(link.path) ? "text-vedanti-orange" : "text-slate-700 hover:text-vedanti-orange"
                  }`}
                  data-testid={`nav-${link.name.toLowerCase().replace(/\s/g, "-")}`}
                >
                  {link.name}
                  {link.submenu && <ChevronDown size={14} />}
                </Link>
                {link.submenu && (
                  <div className="absolute top-full left-0 w-56 bg-white rounded-lg shadow-xl border border-slate-100 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 py-2">
                    {link.submenu.map((sub) => (
                      <Link
                        key={sub.name}
                        to={sub.path}
                        className="block px-4 py-2.5 text-sm text-slate-600 hover:bg-slate-50 hover:text-vedanti-orange transition-colors"
                        data-testid={`nav-sub-${sub.name.toLowerCase().replace(/\s/g, "-")}`}
                      >
                        {sub.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="hidden lg:flex items-center gap-3">
            <a
              href="https://wa.me/918308776505?text=Hi%2C%20I%27m%20interested%20in%20coaching%20at%20Vedanti%20Academy."
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="bg-green-500 text-white hover:bg-green-600 rounded-lg px-4 py-2.5 text-sm font-semibold transition-all inline-flex items-center gap-1.5 shadow-sm"
              data-testid="nav-whatsapp-btn"
            >
              <MessageCircle size={16} /> WhatsApp
            </a>
            <Link
              to="/consultancy"
              className="bg-gradient-to-r from-vedanti-orange to-amber-500 text-white hover:from-vedanti-orange hover:to-vedanti-orange rounded-lg px-5 py-2.5 text-sm font-semibold transition-all shadow-lg shadow-orange-500/20 hover:-translate-y-0.5"
              data-testid="nav-book-consultancy-btn"
            >
              Book Free Consultancy
            </Link>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="lg:hidden p-2 text-slate-700"
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile nav */}
        {mobileOpen && (
          <div className="lg:hidden bg-white border-t border-slate-100 shadow-lg" data-testid="mobile-menu">
            <div className="container-main py-4 space-y-1">
              {navLinks.map((link) => (
                <div key={link.name}>
                  <div className="flex items-center justify-between">
                    <Link
                      to={link.path}
                      onClick={() => !link.submenu && setMobileOpen(false)}
                      className={`block py-2.5 text-base font-medium ${
                        isActive(link.path) ? "text-vedanti-orange" : "text-slate-700"
                      }`}
                      data-testid={`mobile-nav-${link.name.toLowerCase().replace(/\s/g, "-")}`}
                    >
                      {link.name}
                    </Link>
                    {link.submenu && (
                      <button
                        onClick={() => setActiveSubmenu(activeSubmenu === link.name ? null : link.name)}
                        className="p-2 text-slate-500"
                      >
                        <ChevronDown size={16} className={`transition-transform ${activeSubmenu === link.name ? "rotate-180" : ""}`} />
                      </button>
                    )}
                  </div>
                  {link.submenu && activeSubmenu === link.name && (
                    <div className="pl-4 space-y-1 pb-2">
                      {link.submenu.map((sub) => (
                        <Link
                          key={sub.name}
                          to={sub.path}
                          onClick={() => setMobileOpen(false)}
                          className="block py-2 text-sm text-slate-500 hover:text-vedanti-orange"
                        >
                          {sub.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              <div className="pt-3 border-t border-slate-100 space-y-2">
                <Link
                  to="/consultancy"
                  onClick={() => setMobileOpen(false)}
                  className="block text-center bg-gradient-to-r from-vedanti-orange to-amber-500 text-white rounded-lg px-5 py-3 font-semibold"
                  data-testid="mobile-book-consultancy-btn"
                >
                  Book Free Consultancy
                </Link>
                <a
                  href="https://wa.me/918308776505?text=Hi%2C%20I%27m%20interested%20in%20coaching%20at%20Vedanti%20Academy."
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="block text-center bg-green-500 text-white rounded-lg px-5 py-3 font-semibold"
                  data-testid="mobile-whatsapp-btn"
                >
                  <MessageCircle size={16} className="inline mr-2 -mt-0.5" /> WhatsApp Us
                </a>
              </div>
              <div className="pt-2">
                <a href="tel:+918308776505" className="flex items-center gap-2 text-vedanti-navy font-medium py-2">
                  <Phone size={16} /> +91 8308776505
                </a>
              </div>
            </div>
          </div>
        )}
      </nav>
    </>
  );
};
